import type { Task } from '../services/taskService';

import { formatPhaseCredits } from './phaseBuildPresentation';

/** Structural planning rows describe artifacts but do not each incur a charge.
 * A captured flag distinguishes those rows from a real zero-cost snapshot. */
export const hasPlanningTaskCredit = (
  task: Pick<Task, 'task_type' | 'planning_metadata'>,
): boolean => (
  task.task_type === 'planning'
  && task.planning_metadata?.credit_price_captured === true
  && task.planning_metadata?.credit_not_required !== true
);

/** Planning prices are persisted by the backend, so the UI must display that
 * amount instead of applying the current model or plan. */
export const planningTaskCreditAmount = (
  task: Pick<Task, 'prompt_credit'>,
): number => {
  const amount = Number(task.prompt_credit);

  return Number.isFinite(amount) ? Math.max(0, amount) : 0;
};

export const formatPlanningTaskCredits = (
  task: Pick<Task, 'prompt_credit'>,
): string => formatPhaseCredits(planningTaskCreditAmount(task));

export const planningTaskCreditLabel = (
  task: Pick<Task, 'planning_credit_charged'>,
): string => task.planning_credit_charged ? 'Credits used' : 'Credits needed';

export const planningTaskCreditShortLabel = (
  task: Pick<Task, 'planning_credit_charged'>,
): string => task.planning_credit_charged ? 'used' : 'needed';

export const planningTaskCreditTitle = (
  task: Pick<Task, 'planning_credit_charged'>,
): string => task.planning_credit_charged
  ? 'This cost was deducted when the planning task started'
  : 'This saved estimate is finalized for the authenticated billing account when the planning task starts';

/** Every compact row explains its cost, including artifacts billed elsewhere. */
export const planningTaskCreditSummary = (
  task: Pick<Task, 'task_type' | 'prompt_credit' | 'planning_metadata' | 'planning_credit_charged'>,
): { label: string; title: string } => {
  const metadata = task.planning_metadata;

  if (metadata?.credit_not_required === true) {
    return { label: 'No extra credits', title: 'Existing output was reused; no additional generation charge is needed' };
  }
  if (hasPlanningTaskCredit(task) && Number.isFinite(task.prompt_credit) && task.prompt_credit >= 0) {
    return {
      label: `${formatPlanningTaskCredits(task)} cr ${planningTaskCreditShortLabel(task)}`,
      title: planningTaskCreditTitle(task),
    };
  }
  if (metadata?.credit_price_captured !== true && metadata?.parent_key) {
    if (metadata.artifact_type === 'design_page') {
      return {
        label: 'Credits in subtasks',
        title: 'Page generation credits are shown on its section and repair tasks; setup credits are shown on the parent stage',
      };
    }
    if (['user_flow', 'architecture_diagram', 'shared_component'].includes(metadata.artifact_type ?? '')) {
      return { label: 'Credits in parent', title: 'This artifact is covered by its parent planning task; it has no separate charge' };
    }
  }

  return { label: 'Credits unavailable', title: 'No reliable credit amount has been recorded for this task' };
};
